import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { RichTextEditor, RichTextDisplay } from '@/components/ui/RichTextEditor';
import {
  ArrowLeftIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlusIcon,
  LinkIcon,
  EyeIcon,
  LockClosedIcon,
  PhotoIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  investigationsService,
  Investigation,
  TimelineEvent,
  InvestigationNote,
  InvestigationAttachment,
  DataCorrelation,
  InvestigationSubject,
} from '@/services/investigations.service';
import { usersService, User } from '@/services/users.service';
import { authService } from '@/services/auth';
import UploadAttachmentModal from './UploadAttachmentModal';
import FilePreviewModal from './FilePreviewModal';
import { SubjectCard } from './SubjectCard';
import LinkSearchModal from '@/components/common/LinkSearchModal';
import { LinkedSubjectsPanel } from '@/components/subjects/LinkedSubjectsPanel';
import { UserPicker } from '@/components/common/UserPicker';
import { DateInput } from '@/components/ui/DateInput';
import { formatIncidentStatus, formatInvestigationCategory } from '@/utils/labels';
import toast from 'react-hot-toast';
import { SECURITY_CATEGORIES } from '@corporate-security/types';

interface InvestigationDetailsProps {
  investigationId: string;
  onBack?: () => void;
}

const InvestigationDetails: React.FC<InvestigationDetailsProps> = ({
  investigationId,
  onBack,
}) => {
  const [investigation, setInvestigation] = useState<Investigation | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [notes, setNotes] = useState<InvestigationNote[]>([]);
  const [attachments, setAttachments] = useState<InvestigationAttachment[]>([]);
  const [_correlations, setCorrelations] = useState<DataCorrelation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'notes' | 'attachments'>('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [_narrativeLength, setNarrativeLength] = useState(0);
  const [editForm, setEditForm] = useState({
    title: '',
    narrative: '',
    status: 'PENDING' as 'ACTIVE' | 'PENDING' | 'CLOSED' | 'SUSPENDED',
    priority: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL',
    category: '',
    dueDate: '',
    estimatedCompletionDate: '',
    leadInvestigatorId: '',
    subjects: [] as InvestigationSubject[],
  });
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Form state for adding timeline events
  const [showTimelineForm, setShowTimelineForm] = useState(false);
  const [timelineForm, setTimelineForm] = useState({
    type: '',
    title: '',
    description: ''
  });

  // Form state for adding notes
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteForm, setNoteForm] = useState({
    content: '',
    isConfidential: false
  });

  // Form state for adding correlations
  const [_showCorrelationForm, _setShowCorrelationForm] = useState(false);
  const [_correlationForm, _setCorrelationForm] = useState({
    correlationType: '',
    sourceType: '',
    targetType: '',
    description: '',
    confidence: 0.5
  });

  // Upload attachment modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<InvestigationAttachment | null>(null);

  // Linked Incidents state
  const [showLinkIncidentModal, setShowLinkIncidentModal] = useState(false);
  const [linkedIncidents, setLinkedIncidents] = useState<Array<{ id: string; incidentNumber: string; title: string; status: string }>>([]);

  useEffect(() => {
    fetchInvestigationDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationId]);

  const fetchInvestigationDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the investigations service to fetch data
      const [investigationData, attachmentsData, correlationsData] = await Promise.all([
        investigationsService.getInvestigationById(investigationId),
        investigationsService.getAttachments(investigationId).catch(() => []),
        investigationsService.getDataCorrelations(investigationId).catch(() => [])
      ]);

      setInvestigation(investigationData);
      setTimeline(investigationData.timeline ?? []);
      setNotes(investigationData.notes ?? []);
      setAttachments(attachmentsData);
      setCorrelations(correlationsData);
      // Populate linked incidents from enriched response
      const linked = (investigationData as unknown as Record<string, unknown>).linkedIncidents as Array<{ id: string; incidentNumber: string; title: string; status: string }> | undefined;
      setLinkedIncidents(linked ?? []);
    } catch (err) {
      console.error('Failed to fetch investigation details:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch investigation details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PENDING': return 'warning';
      case 'CLOSED': return 'secondary';
      case 'SUSPENDED': return 'danger';
      default: return 'secondary';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'CRITICAL': return 'danger';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'primary';
      case 'LOW': return 'success';
      default: return 'secondary';
    }
  };

  const getTimelineIcon = (type: string) => {
    switch (type) {
      case 'CREATED': return CheckCircleIcon;
      case 'ASSIGNED':
      case 'LEAD_INVESTIGATOR_CHANGED': return UserIcon;
      case 'EVIDENCE_ADDED': return DocumentTextIcon;
      case 'STATUS_CHANGED': return ExclamationTriangleIcon;
      case 'NOTE_ADDED': return DocumentTextIcon;
      case 'CORRELATION_FOUND': return LinkIcon;
      case 'COMPLETED': return CheckCircleIcon;
      default: return ClockIcon;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // DATE-only columns (e.g. subjectDob) are stored without a time component.
  // Parsing them as a plain Date and using toLocaleDateString shifts the day
  // backward in non-UTC timezones (midnight UTC = prior evening locally).
  // Splitting on 'T' and treating the date portion as UTC avoids the shift.
  const formatDateOnly = (dateString: string) => {
    const [datePart] = dateString.split('T');
    const parts = (datePart ?? dateString).split('-').map(Number);
    const year = parts[0] ?? 0;
    const month = parts[1] ?? 1;
    const day = parts[2] ?? 1;
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  const formatDaysAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) { return '1 day ago'; }
    if (diffDays < 30) { return `${diffDays} days ago`; }
    if (diffDays < 365) { return `${Math.floor(diffDays / 30)} months ago`; }
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const handleEditClick = () => {
    if (investigation) {
      // Use subjects array, or convert subjectInformation for backward compatibility
      const subjects = investigation.subjects && investigation.subjects.length > 0
        ? investigation.subjects.map(s => ({
          id: s.id,
          name: s.name ?? '',
          email: s.email ?? '',
          phone: s.phone ?? '',
          role: s.role ?? '',
          department: s.department ?? '',
          notes: s.notes ?? '',
          isPrimary: s.isPrimary ?? false,
        }))
        : investigation.subjectInformation
          ? [{
            name: investigation.subjectInformation.name ?? '',
            email: investigation.subjectInformation.email ?? '',
            phone: investigation.subjectInformation.phone ?? '',
            role: investigation.subjectInformation.role ?? '',
            department: investigation.subjectInformation.department ?? '',
            notes: investigation.subjectInformation.notes ?? '',
            isPrimary: true,
          }]
          : [];

      setEditForm({
        title: investigation.title,
        narrative: investigation.narrative,
        status: investigation.status,
        priority: investigation.priority,
        category: investigation.category,
        dueDate: investigation.dueDate ?? '',
        estimatedCompletionDate: investigation.estimatedCompletionDate ?? '',
        leadInvestigatorId: investigation.leadInvestigator?.id ?? '',
        subjects,
      });
      setNarrativeLength(investigation.narrative.length);
      setIsEditing(true);

      // Lazy-load users for the Lead Investigator picker
      if (users.length === 0 && !loadingUsers) {
        setLoadingUsers(true);
        usersService
          .getUsers({ isActive: true })
          .then((response) => setUsers(response.users))
          .catch((err) => {
            console.error('Failed to load users for lead investigator picker:', err);
          })
          .finally(() => setLoadingUsers(false));
      }
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNarrativeLength(0);
    setEditForm({
      title: '',
      narrative: '',
      status: 'PENDING',
      priority: 'MEDIUM',
      category: '',
      dueDate: '',
      estimatedCompletionDate: '',
      leadInvestigatorId: '',
      subjects: [],
    });
  };

  // Subject handlers for multiple subjects
  const handleSubjectChange = (index: number, field: keyof InvestigationSubject, value: string | boolean) => {
    setEditForm(prev => ({
      ...prev,
      subjects: prev.subjects.map((subject, i) =>
        i === index ? { ...subject, [field]: value } : subject
      ),
    }));
  };

  const handleAddSubject = () => {
    setEditForm(prev => ({
      ...prev,
      subjects: [...prev.subjects, { isPrimary: prev.subjects.length === 0 }],
    }));
  };

  const handleRemoveSubject = (index: number) => {
    setEditForm(prev => {
      const newSubjects = prev.subjects.filter((_, i) => i !== index);
      // Ensure at least one subject is primary if any exist
      if (newSubjects.length > 0 && !newSubjects.some(s => s.isPrimary)) {
        newSubjects[0].isPrimary = true;
      }
      return { ...prev, subjects: newSubjects };
    });
  };

  const handleSetPrimarySubject = (index: number) => {
    setEditForm(prev => ({
      ...prev,
      subjects: prev.subjects.map((subject, i) => ({
        ...subject,
        isPrimary: i === index,
      })),
    }));
  };

  const handleSave = async () => {
    if (!investigation) { return; }

    try {
      setIsSaving(true);

      // Filter subjects to only include those with content
      const subjectsWithContent = editForm.subjects.filter(subject =>
        subject.name ?? subject.email ?? subject.phone ?? subject.role ?? subject.department ?? subject.notes
      ).map(subject => ({
        id: subject.id,
        name: subject.name?.trim() ?? undefined,
        email: subject.email?.trim() ?? undefined,
        phone: subject.phone?.trim() ?? undefined,
        role: subject.role?.trim() ?? undefined,
        department: subject.department?.trim() ?? undefined,
        notes: subject.notes?.trim() ?? undefined,
        isPrimary: subject.isPrimary,
      }));

      const currentUser = authService.getStoredUser();
      const updateData: Parameters<typeof investigationsService.updateInvestigation>[1] = {
        title: editForm.title,
        narrative: editForm.narrative,
        status: editForm.status,
        priority: editForm.priority,
        category: editForm.category,
        dueDate: editForm.dueDate ?? undefined,
        estimatedCompletionDate: editForm.estimatedCompletionDate ?? undefined,
        subjects: subjectsWithContent,
      };

      // Only include leadInvestigator in the payload if it changed,
      // so the backend can detect the change and emit a Timeline event.
      if (
        editForm.leadInvestigatorId &&
        editForm.leadInvestigatorId !== investigation.leadInvestigator?.id
      ) {
        const selectedUser = users.find((u) => u.id === editForm.leadInvestigatorId);
        if (!selectedUser) {
          setError('Selected lead investigator not found. Please try again.');
          setIsSaving(false);
          return;
        }
        updateData.leadInvestigator = {
          id: selectedUser.id,
          firstName: selectedUser.firstName,
          lastName: selectedUser.lastName,
          email: selectedUser.email,
        };
      }

      if (currentUser) {
        updateData.user = {
          id: currentUser.id,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
          email: currentUser.email,
        };
      }

      await investigationsService.updateInvestigation(investigation.id, updateData);
      setIsEditing(false);
      // Refresh investigation data
      await fetchInvestigationDetails();
    } catch (err) {
      console.error('Failed to update investigation:', err);
      setError(err instanceof Error ? err.message : 'Failed to update investigation');
    } finally {
      setIsSaving(false);
    }
  };

  // Handler for adding timeline event
  const handleAddTimelineEvent = async () => {
    if (!investigation || !timelineForm.type || !timelineForm.title) { return; }

    try {
      // Get current user information
      const currentUser = authService.getStoredUser();
      if (!currentUser) {
        setError('User information not found. Please log in again.');
        return;
      }

      await investigationsService.addTimelineEvent(investigation.id, {
        type: timelineForm.type as 'COMPLETED' | 'CREATED' | 'ASSIGNED' | 'EVIDENCE_ADDED' | 'STATUS_CHANGED' | 'NOTE_ADDED' | 'CORRELATION_FOUND',
        title: timelineForm.title,
        description: timelineForm.description,
        user: {
          id: currentUser.id,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
          email: currentUser.email,
        },
      });

      // Reset form and hide it
      setTimelineForm({ type: '', title: '', description: '' });
      setShowTimelineForm(false);

      // Refresh investigation data
      await fetchInvestigationDetails();
    } catch (err) {
      console.error('Failed to add timeline event:', err);
      setError(err instanceof Error ? err.message : 'Failed to add timeline event');
    }
  };

  // Handler for adding note
  const handleAddNote = async () => {
    if (!investigation || !noteForm.content) { return; }

    try {
      // Get current user information
      const currentUser = authService.getStoredUser();
      if (!currentUser) {
        setError('User information not found. Please log in again.');
        return;
      }

      await investigationsService.addInvestigationNote(investigation.id, {
        content: noteForm.content,
        isConfidential: noteForm.isConfidential,
        author: {
          id: currentUser.id,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
          email: currentUser.email,
        },
      });

      // Reset form and hide it
      setNoteForm({ content: '', isConfidential: false });
      setShowNoteForm(false);

      // Refresh investigation data
      await fetchInvestigationDetails();
    } catch (err) {
      console.error('Failed to add note:', err);
      setError(err instanceof Error ? err.message : 'Failed to add note');
    }
  };

  // Handler for adding correlation (currently unused, kept for future implementation)
  // @ts-expect-error Unused function kept for future implementation
  const _handleAddCorrelation = async () => {
    if (!investigation || !_correlationForm.correlationType || !_correlationForm.description) { return; }

    try {
      await investigationsService.addDataCorrelation(investigation.id, {
        correlationType: _correlationForm.correlationType as 'RELATED' | 'CAUSAL' | 'TEMPORAL' | 'GEOGRAPHIC' | 'PERSONNEL',
        sourceType: _correlationForm.sourceType as 'INCIDENT' | 'THREAT' | 'INVESTIGATION' | 'PERSON',
        targetType: _correlationForm.targetType as 'INCIDENT' | 'THREAT' | 'INVESTIGATION' | 'PERSON',
        description: _correlationForm.description,
        confidence: _correlationForm.confidence,
        sourceId: '', // Required fields - will be set by service if not provided
        targetId: '',
      });

      // Reset form and hide it
      _setCorrelationForm({
        correlationType: '',
        sourceType: '',
        targetType: '',
        description: '',
        confidence: 0.5
      });
      _setShowCorrelationForm(false);

      // Refresh investigation data
      await fetchInvestigationDetails();
    } catch (err) {
      console.error('Failed to add correlation:', err);
      setError(err instanceof Error ? err.message : 'Failed to add correlation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error ?? !investigation) {
    return (
      <div className="text-center py-8">
        <ExclamationTriangleIcon className="w-12 h-12 text-danger-500 mx-auto mb-4" />
        <p className="text-danger-600 dark:text-danger-400">{error ?? 'Investigation not found'}</p>
        <Button variant="outline" className="mt-4" onClick={onBack}>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeftIcon className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
                {investigation.title}
              </h1>
              <span className="text-sm font-mono text-secondary-500 dark:text-secondary-400">
                #{investigation.caseNumber}
              </span>
            </div>
            <div className="flex items-center space-x-4 mt-2">
              <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${getStatusColor(investigation.status)}-100 text-${getStatusColor(investigation.status)}-700 dark:bg-${getStatusColor(investigation.status)}-900/20 dark:text-${getStatusColor(investigation.status)}-400`}>
                {investigation.status}
              </span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${getPriorityColor(investigation.priority)}-100 text-${getPriorityColor(investigation.priority)}-700 dark:bg-${getPriorityColor(investigation.priority)}-900/20 dark:text-${getPriorityColor(investigation.priority)}-400`}>
                {investigation.priority} Priority
              </span>
              <span className="text-sm text-secondary-600 dark:text-secondary-400">
                {formatInvestigationCategory(investigation.category)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex space-x-3">
          {!isEditing ? (
            <>
              <Button size="sm" onClick={handleEditClick}>
                Edit Case
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-secondary-200 dark:border-secondary-700">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'overview', label: 'Overview', icon: DocumentTextIcon },
            { key: 'timeline', label: 'Timeline', icon: ClockIcon },
            { key: 'notes', label: 'Notes', icon: DocumentTextIcon },
            { key: 'attachments', label: 'Attachments', icon: PhotoIcon },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as 'overview' | 'timeline' | 'notes' | 'attachments')}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.key
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300 dark:text-secondary-400 dark:hover:text-secondary-300'
                  }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Case Information */}
            <div className="lg:col-span-2 space-y-6">
              {isEditing ? (
                <Card>
                  <CardHeader>
                    <CardTitle>Edit Investigation</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                        Title
                      </label>
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                      />
                    </div>
                    <RichTextEditor
                      label="Narrative"
                      value={editForm.narrative}
                      onChange={(html) => setEditForm({ ...editForm, narrative: html })}
                      minHeight={150}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                          Status
                        </label>
                        <select
                          value={editForm.status}
                          onChange={(e) => setEditForm({ ...editForm, status: e.target.value as 'ACTIVE' | 'PENDING' | 'CLOSED' | 'SUSPENDED' })}
                          className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                        >
                          <option value="PENDING">Pending</option>
                          <option value="ACTIVE">Active</option>
                          <option value="SUSPENDED">Suspended</option>
                          <option value="CLOSED">Closed</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                          Priority
                        </label>
                        <select
                          value={editForm.priority}
                          onChange={(e) => setEditForm({ ...editForm, priority: e.target.value as 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' })}
                          className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                        >
                          <option value="LOW">Low</option>
                          <option value="MEDIUM">Medium</option>
                          <option value="HIGH">High</option>
                          <option value="CRITICAL">Critical</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                        Category
                      </label>
                      <select
                        value={editForm.category}
                        onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                        className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                      >
                        <option value="">Select Category</option>
                        {SECURITY_CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <UserPicker
                        label="Lead Investigator"
                        value={editForm.leadInvestigatorId}
                        onChange={(picked) => {
                          setEditForm({
                            ...editForm,
                            leadInvestigatorId: picked?.id ?? '',
                          });
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <DateInput
                          label="Due Date"
                          value={editForm.dueDate}
                          onChange={(value) => setEditForm({ ...editForm, dueDate: value })}
                        />
                      </div>
                      <div>
                        <DateInput
                          label="Estimated Completion"
                          value={editForm.estimatedCompletionDate}
                          onChange={(value) => setEditForm({ ...editForm, estimatedCompletionDate: value })}
                        />
                      </div>
                    </div>

                    {/* Subjects Section */}
                    <div className="pt-4 mt-4 border-t border-secondary-200 dark:border-secondary-700">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-medium text-secondary-900 dark:text-white">
                            Subjects
                          </h3>
                          <p className="text-sm text-secondary-600 dark:text-secondary-400">
                            Information about the person(s) or entity(ies) under investigation
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleAddSubject}
                        >
                          <PlusIcon className="w-4 h-4 mr-2" />
                          Add Subject
                        </Button>
                      </div>
                      {editForm.subjects.length === 0 ? (
                        <div className="text-center py-6 border-2 border-dashed border-secondary-200 dark:border-secondary-700 rounded-lg">
                          <UserIcon className="w-10 h-10 mx-auto text-secondary-400 mb-2" />
                          <p className="text-secondary-500 dark:text-secondary-400 mb-3">No subjects</p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleAddSubject}
                          >
                            <PlusIcon className="w-4 h-4 mr-2" />
                            Add First Subject
                          </Button>
                        </div>
                      ) : (
                        editForm.subjects.map((subject, index) => (
                          <SubjectCard
                            key={subject.id ?? index}
                            subject={subject}
                            index={index}
                            isEditing={true}
                            canDelete={true}
                            onChange={handleSubjectChange}
                            onDelete={handleRemoveSubject}
                            onSetPrimary={handleSetPrimarySubject}
                          />
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle>Case Narrative</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <RichTextDisplay
                        content={investigation.narrative}
                        className="text-secondary-700 dark:text-secondary-300"
                        emptyMessage="No narrative provided."
                      />
                    </CardContent>
                  </Card>

                  {/* Renders only for legacy investigations (those with rows in the
                      InvestigationSubject array — populated by the public intake form).
                      New flows use LinkedSubjectsPanel. TODO: migrate the legacy rows
                      into the Subject registry and remove this panel — see
                      docs/superpowers/specs/2026-04-30-post-review-fixes-design.md fix G. */}
                  {investigation.subjects && investigation.subjects.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>
                          Intake Subject
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {investigation.subjects.map((subject, index) => (
                          <SubjectCard
                            key={subject.id ?? index}
                            subject={subject}
                            index={index}
                            isEditing={false}
                            canDelete={false}
                            onChange={() => { }}
                            onDelete={() => { }}
                            onSetPrimary={() => { }}
                          />
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              <LinkedSubjectsPanel caseId={investigation.id} caseType="INVESTIGATION" />

              <Card>
                <CardHeader>
                  <CardTitle>Evidence Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                        {investigation.evidence.documentsCount}
                      </div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">
                        Documents
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                        {investigation.evidence.mediaCount}
                      </div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">
                        Media Files
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                        {investigation.evidence.witnessStatements}
                      </div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">
                        Witness Statements
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {investigation.relatedCases.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <LinkIcon className="w-5 h-5" />
                      <span>Related Cases</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {investigation.relatedCases.map((caseId) => (
                        <div
                          key={caseId}
                          className="flex items-center justify-between p-3 border border-secondary-200 dark:border-secondary-700 rounded-lg"
                        >
                          <span className="font-medium">{caseId}</span>
                          <Button variant="outline" size="sm">
                            View Case
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {investigation.source === 'PUBLIC_INTAKE' && (
                <Card>
                  <CardHeader>
                    <CardTitle>Public Intake</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {investigation.referenceNumber && (
                      <div>
                        <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                          Reference Number
                        </label>
                        <div className="mt-1 font-mono text-sm">
                          {investigation.referenceNumber}
                        </div>
                      </div>
                    )}
                    {investigation.requesterName && (
                      <div>
                        <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                          Requester
                        </label>
                        <div className="mt-1 text-sm">
                          {investigation.requesterName}
                          {investigation.requesterRole ? ` — ${investigation.requesterRole}` : ''}
                        </div>
                      </div>
                    )}
                    {investigation.requesterOrganization && (
                      <div>
                        <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                          Organization
                        </label>
                        <div className="mt-1 text-sm">
                          {investigation.requesterOrganization}
                        </div>
                      </div>
                    )}
                    {investigation.requesterEmail && (
                      <div>
                        <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                          Email
                        </label>
                        <div className="mt-1 text-sm">
                          <a
                            href={`mailto:${investigation.requesterEmail}`}
                            className="text-primary-600 dark:text-primary-400 hover:underline break-all"
                          >
                            {investigation.requesterEmail}
                          </a>
                        </div>
                      </div>
                    )}
                    {investigation.requesterPhone && (
                      <div>
                        <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                          Phone
                        </label>
                        <div className="mt-1 text-sm">
                          <a
                            href={`tel:${investigation.requesterPhone}`}
                            className="text-primary-600 dark:text-primary-400 hover:underline"
                          >
                            {investigation.requesterPhone}
                          </a>
                        </div>
                      </div>
                    )}
                    {investigation.investigationType && (
                      <div>
                        <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                          Investigation Type
                        </label>
                        <div className="mt-1 text-sm">
                          {investigation.investigationType.replace(/_/g, ' ')}
                        </div>
                      </div>
                    )}
                    {investigation.urgency && (
                      <div>
                        <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                          Urgency
                        </label>
                        <div className="mt-1 text-sm">{investigation.urgency}</div>
                      </div>
                    )}
                    {investigation.subjectType && (
                      <div>
                        <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                          Subject Type
                        </label>
                        <div className="mt-1 text-sm">{investigation.subjectType}</div>
                      </div>
                    )}
                    {investigation.subjectAddress && (
                      <div>
                        <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                          Subject Address
                        </label>
                        <div className="mt-1 text-sm whitespace-pre-line">
                          {investigation.subjectAddress}
                        </div>
                      </div>
                    )}
                    {investigation.subjectDob && (
                      <div>
                        <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                          Subject DOB
                        </label>
                        <div className="mt-1 text-sm">
                          {formatDateOnly(investigation.subjectDob)}
                        </div>
                      </div>
                    )}
                    {investigation.subjectIdentifiers && (
                      <div>
                        <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                          Subject Identifiers
                        </label>
                        <div className="mt-1 text-sm whitespace-pre-line">
                          {investigation.subjectIdentifiers}
                        </div>
                      </div>
                    )}
                    {investigation.desiredOutcome && (
                      <div>
                        <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                          Desired Outcome
                        </label>
                        <div className="mt-1 text-sm whitespace-pre-line">
                          {investigation.desiredOutcome}
                        </div>
                      </div>
                    )}
                    {investigation.howHeardAboutUs && (
                      <div>
                        <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                          Heard About Us
                        </label>
                        <div className="mt-1 text-sm">
                          {investigation.howHeardAboutUs}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              <Card>
                <CardHeader>
                  <CardTitle>Case Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                      Lead Investigator
                    </label>
                    <div className="flex items-center space-x-2 mt-1">
                      <UserIcon className="w-4 h-4 text-secondary-400" />
                      <span>
                        {investigation.leadInvestigator
                          ? `${investigation.leadInvestigator.firstName} ${investigation.leadInvestigator.lastName}`.trim()
                          : 'Unassigned'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                      Created
                    </label>
                    <div className="flex items-center space-x-2 mt-1">
                      <CalendarIcon className="w-4 h-4 text-secondary-400" />
                      <span className="text-sm">{formatDate(investigation.createdAt)}</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                      Last Updated
                    </label>
                    <div className="flex items-center space-x-2 mt-1">
                      <ClockIcon className="w-4 h-4 text-secondary-400" />
                      <span className="text-sm">{formatDaysAgo(investigation.updatedAt)}</span>
                    </div>
                  </div>

                  {investigation.dueDate && (
                    <div>
                      <label className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                        Due Date
                      </label>
                      <div className="flex items-center space-x-2 mt-1">
                        <CalendarIcon className="w-4 h-4 text-warning-500" />
                        <span className="text-sm text-warning-600 dark:text-warning-400">
                          {formatDate(investigation.dueDate)}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Linked Incidents */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Linked Incidents</CardTitle>
                    <button
                      onClick={() => setShowLinkIncidentModal(true)}
                      className="text-xs px-2 py-1 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
                    >
                      + Link
                    </button>
                  </div>
                </CardHeader>
                <CardContent>
                  {linkedIncidents.length === 0 ? (
                    <p className="text-sm text-secondary-500 dark:text-secondary-400 text-center py-4">
                      No linked incidents
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {linkedIncidents.map((inc) => (
                        <div
                          key={inc.id}
                          className="flex items-center justify-between p-2 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-800/50 transition-colors group"
                        >
                          <a
                            href={`/security/incidents/${inc.id}`}
                            className="flex-1 min-w-0"
                          >
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-xs font-mono text-primary-600 dark:text-primary-400">
                                {inc.incidentNumber}
                              </span>
                              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                                inc.status === 'OPEN' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                                inc.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                                inc.status === 'RESOLVED' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                inc.status === 'PENDING_CLOSURE_APPROVAL' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
                                inc.status === 'REJECTED_CLOSURE' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                                'bg-secondary-100 text-secondary-800 dark:bg-secondary-900/30 dark:text-secondary-300'
                              }`}>
                                {formatIncidentStatus(inc.status)}
                              </span>
                            </div>
                            <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                              {inc.title}
                            </p>
                          </a>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await investigationsService.unlinkIncident(investigationId, inc.id);
                                setLinkedIncidents((prev) => prev.filter((i) => i.id !== inc.id));
                                toast.success('Incident unlinked');
                              } catch {
                                toast.error('Failed to unlink incident');
                              }
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-secondary-400 hover:text-danger-500 transition-all flex-shrink-0 ml-2"
                            title="Unlink incident"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'timeline' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Investigation Timeline</span>
                <Button size="sm" onClick={() => setShowTimelineForm(!showTimelineForm)}>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  {showTimelineForm ? 'Cancel' : 'Add Event'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {showTimelineForm && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg bg-secondary-50 dark:bg-secondary-800/50"
                >
                  <h4 className="font-medium text-secondary-900 dark:text-white mb-4">Add Timeline Event</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                        Event Type
                      </label>
                      <select
                        value={timelineForm.type}
                        onChange={(e) => setTimelineForm({ ...timelineForm, type: e.target.value })}
                        className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-md bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white"
                      >
                        <option value="">Select type...</option>
                        <option value="CREATED">Created</option>
                        <option value="STATUS_CHANGED">Status Changed</option>
                        <option value="NOTE_ADDED">Note Added</option>
                        <option value="EVIDENCE_ADDED">Evidence Added</option>
                        <option value="INTERVIEW">Interview</option>
                        <option value="FINDING">Finding</option>
                        <option value="UPDATE">Update</option>
                        <option value="COMPLETED">Completed</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                        Title
                      </label>
                      <input
                        type="text"
                        value={timelineForm.title}
                        onChange={(e) => setTimelineForm({ ...timelineForm, title: e.target.value })}
                        className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-md bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white"
                        placeholder="Event title..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                        Description
                      </label>
                      <textarea
                        value={timelineForm.description}
                        onChange={(e) => setTimelineForm({ ...timelineForm, description: e.target.value })}
                        className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-md bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white"
                        placeholder="Event description..."
                        rows={3}
                      />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button variant="outline" onClick={() => setShowTimelineForm(false)}>
                        Cancel
                      </Button>
                      <Button
                        onClick={handleAddTimelineEvent}
                        disabled={!timelineForm.type || !timelineForm.title}
                      >
                        Add Event
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
              <div className="space-y-4">
                {timeline.map((event, index) => {
                  const Icon = getTimelineIcon(event.type);
                  return (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start space-x-4"
                    >
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                        <Icon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-secondary-900 dark:text-white">
                            {event.title}
                          </h4>
                          <span className="text-sm text-secondary-500 dark:text-secondary-400">
                            {formatDate(event.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                          {event.description}
                        </p>
                        <div className="text-xs text-secondary-500 dark:text-secondary-500 mt-2">
                          by {event.user.firstName} {event.user.lastName}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'notes' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Investigation Notes</span>
                <Button size="sm" onClick={() => setShowNoteForm(!showNoteForm)}>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  {showNoteForm ? 'Cancel' : 'Add Note'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {showNoteForm && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg bg-secondary-50 dark:bg-secondary-800/50"
                >
                  <h4 className="font-medium text-secondary-900 dark:text-white mb-4">Add Note</h4>
                  <div className="space-y-4">
                    <RichTextEditor
                      label="Note Content"
                      value={noteForm.content}
                      onChange={(html) => setNoteForm({ ...noteForm, content: html })}
                      placeholder="Enter note content..."
                      minHeight={120}
                    />
                    <div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={noteForm.isConfidential}
                          onChange={(e) => setNoteForm({ ...noteForm, isConfidential: e.target.checked })}
                          className="w-4 h-4 text-primary-600 border-secondary-300 rounded"
                        />
                        <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                          Mark as confidential
                        </span>
                        <LockClosedIcon className="w-4 h-4 text-warning-500" />
                      </label>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button variant="outline" onClick={() => setShowNoteForm(false)}>
                        Cancel
                      </Button>
                      <Button
                        onClick={handleAddNote}
                        disabled={!noteForm.content}
                      >
                        Add Note
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
              <div className="space-y-4">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className="p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">
                          {note.author.firstName} {note.author.lastName}
                        </span>
                        {note.isConfidential && (
                          <LockClosedIcon className="w-4 h-4 text-warning-500" />
                        )}
                      </div>
                      <span className="text-sm text-secondary-500 dark:text-secondary-400">
                        {formatDate(note.createdAt)}
                      </span>
                    </div>
                    <RichTextDisplay
                      content={note.content}
                      className="text-secondary-700 dark:text-secondary-300"
                      emptyMessage=""
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'attachments' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <PhotoIcon className="w-5 h-5" />
                  <span>Attachments</span>
                </div>
                <Button size="sm" onClick={() => setShowUploadModal(true)}>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Upload File
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {attachments.length === 0 ? (
                <div className="text-center py-8 text-secondary-500 dark:text-secondary-400">
                  <PhotoIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No attachments yet</p>
                  <p className="text-sm">Upload files to add evidence to this investigation</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-800/50"
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <DocumentTextIcon className="w-8 h-8 text-primary-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-secondary-900 dark:text-white truncate">
                            {attachment.originalFilename}
                          </h4>
                          <div className="flex items-center space-x-3 text-xs text-secondary-500 dark:text-secondary-400">
                            <span>{(attachment.fileSize / 1024).toFixed(1)} KB</span>
                            <span>•</span>
                            <span>{attachment.evidenceType}</span>
                            {attachment.virusScanStatus === 'CLEAN' && (
                              <>
                                <span>•</span>
                                <span className="flex items-center text-success-600 dark:text-success-400">
                                  <CheckCircleIcon className="w-3 h-3 mr-1" />
                                  Scanned
                                </span>
                              </>
                            )}
                            {attachment.virusScanStatus === 'PENDING' && (
                              <>
                                <span>•</span>
                                <span className="text-warning-600 dark:text-warning-400">Scanning...</span>
                              </>
                            )}
                          </div>
                          {attachment.description && (
                            <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                              {attachment.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedAttachment(attachment);
                            setShowPreviewModal(true);
                          }}
                          disabled={attachment.virusScanStatus !== 'CLEAN'}
                        >
                          <EyeIcon className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={attachment.virusScanStatus !== 'CLEAN'}
                          onClick={async () => {
                            try {
                              await investigationsService.downloadAttachment(attachment.id);
                            } catch (err) {
                              console.error('Failed to download attachment:', err);
                              setError(err instanceof Error ? err.message : 'Failed to download attachment');
                            }
                          }}
                        >
                          <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            // eslint-disable-next-line no-alert
                            if (confirm('Are you sure you want to delete this attachment?')) {
                              try {
                                await investigationsService.deleteAttachment(attachment.id);
                                await fetchInvestigationDetails();
                              } catch (err) {
                                console.error('Failed to delete attachment:', err);
                                setError(err instanceof Error ? err.message : 'Failed to delete attachment');
                              }
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Upload Attachment Modal */}
      {investigation && (
        <UploadAttachmentModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          investigationId={investigation.id}
          onUploadComplete={fetchInvestigationDetails}
        />
      )}

      {/* File Preview Modal */}
      {selectedAttachment && (
        <FilePreviewModal
          isOpen={showPreviewModal}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedAttachment(null);
          }}
          attachment={selectedAttachment}
          downloadUrl={investigationsService.getAttachmentUrl(selectedAttachment.id)}
        />
      )}

      {/* Link Incident Modal */}
      <LinkSearchModal
        isOpen={showLinkIncidentModal}
        onClose={() => setShowLinkIncidentModal(false)}
        type="incident"
        excludeIds={linkedIncidents.map((i) => i.id)}
        onSelect={async (incidentId) => {
          try {
            await investigationsService.linkIncident(investigationId, incidentId);
            // Re-fetch to get updated linked incidents with summaries
            await fetchInvestigationDetails();
            toast.success('Incident linked');
          } catch (err) {
            toast.error('Failed to link incident');
            throw err; // re-throw so LinkSearchModal keeps the modal open for retry
          }
        }}
      />

    </div>
  );
};

export default InvestigationDetails;